// ============================================================
// applyHits/effects - what the weapon does beyond its damage.
//
// The weapon-special registry, Harrying, afflictions, inflicted
// exposures, hallucinogens, the target's own afflicting exposures and
// their spent uses, then the force field and the power field.
//
// ROLL ORDER CONTRACT: the registry's specials, then afflictions, then
// exposures, then hallucinogens - each APPENDS its dice to the seeded
// sequence rather than shuffling into it.
// ============================================================
import { processExposures } from '../../engine/exposureEffects';
import { processHallucinogens } from '../../engine/hallucination';
import { consumeExposureUses } from '../../engine/exposures';
import { afflictionsFromExposures } from '../../engine/exposureAfflict';
import { decodeCond } from '../../engine/conditions';
import { mergeCondition, hasWS, getWSP, processAllWeaponEffects } from '../../engine/index';
import { processAfflictions } from '../../engine/afflictions';
import { isConditionImmune } from '../../engine/postHitEffects';
import type { WeaponEffectContext, WeaponEffectResult } from '../../engine/weaponEffects';
import type { ForceField } from '../../types';
import { narr, narrEvent, replaceAll, type HitLedger } from './ledger';

/** What every effect processor reads. `newConds` and `newArmour` are the ledger's, shared by reference. */
function effectContext(ledger: HitLedger): WeaponEffectContext {
  const { data, tgt } = ledger;
  return {
    target: tgt,
    hits: data.hitsAfterDefence,
    dos: data.dos,
    specialRules: ledger.sp,
    isMachine: (tgt.machineAP || 0) > 0,
    stacking: ledger.stacking,
    attackerTeamTag: data.attackerTeamTag ?? null,
    attackerSideTag: data.attackerSideTag ?? null,
    attackerExposures: data.attackerExposures ?? [],
    newConds: ledger.newConds,
    newArmour: ledger.newArmour,
    slot: { ...data.slot },
    parryResult: data.parryResult,
    dealtDamage: data.hitsAfterDefence.some((h) => !h.ffBlocked && h.effectiveDamage > 0),
    targetKilled: ledger.targetKilled,
    haywireRoll: data.haywireRoll ?? 0,
  };
}

/**
 * Harrying(X) (v8.287.0): a target that DODGED this weapon dances
 * off-balance - -X to its own attacks for a round. Fires on the dodge, not
 * on damage: the whole point is punishing the successful escape.
 */
function applyHarrying(ledger: HitLedger): void {
  const harrying = getWSP(ledger.sp, 'Harrying');
  if (harrying <= 0) return;
  const steps = (ledger.data.defenceChainSteps as { step?: { type?: string }; success?: boolean }[] | undefined) || [];
  const dodged = steps.some((s) => (s.step?.type === 'dodge' || s.step?.type === 'step_aside_dodge') && s.success);
  if (!dodged) return;
  mergeCondition(ledger.newConds, `Pen:ws+bs=-${harrying}:1`, ledger.stacking);
  narr(ledger, '🌀 Harrying: the dodge costs balance - -' + harrying + ' to own attacks for a round.');
}

/** Fold one processor's lines and events into the registry's result. */
function appendResult(into: WeaponEffectResult, more: Pick<WeaponEffectResult, 'narrativeNotes' | 'effectDetails'>) {
  into.narrativeNotes.push(...more.narrativeNotes);
  into.effectDetails.push(...more.effectDetails);
}

/**
 * The registry, Harrying, then the processors that append after it -
 * afflictions (the only one whose extra damage counts), exposures the
 * weapon inflicts (v8.352.0), hallucinogens (v8.358.0, per victim).
 */
function runProcessors(ledger: HitLedger): WeaponEffectResult {
  const ctx = effectContext(ledger);
  const result = processAllWeaponEffects(ctx);
  applyHarrying(ledger);
  const afflictions = processAfflictions(ctx);
  appendResult(result, afflictions);
  result.extraDamage += afflictions.extraDamage;
  appendResult(result, processExposures(ctx));
  appendResult(result, processHallucinogens(ctx));
  return result;
}

/**
 * AFFLICT (v8.368.4): what they wear lays a condition on THEM when a hit
 * lands - Soaked catches fire from an Energy hit. Read before the uses are
 * spent, so this burst still enjoys it; immunity and the stacking rule are
 * the same as for any applied condition.
 */
function layExposureAfflictions(ledger: HitLedger): void {
  const { data, tgt } = ledger;
  const laid = afflictionsFromExposures(
    { conditions: ledger.newConds, exposures: tgt.exposures, teamTag: tgt.teamTag, sideTag: tgt.sideTag },
    {
      teamTag: data.attackerTeamTag ?? null,
      sideTag: data.attackerSideTag ?? null,
      auras: data.attackerAuras,
      isMelee: data.slot.weaponType === 'melee',
    },
    data.slot.damageType,
  );
  for (const { code, label } of laid) layCondition(ledger, code, label);
}

/** One afflicting exposure's condition: refused by an immunity (said), or merged and announced. */
function layCondition(ledger: HitLedger, code: string, label: string): void {
  const immunity = isConditionImmune(code, ledger.tgt, ledger.sp);
  if (immunity.immune) {
    if (immunity.reason) narr(ledger, immunity.reason);
    return;
  }
  mergeCondition(ledger.newConds, code, ledger.stacking);
  const text = '◎ ' + label + ': ' + (decodeCond(code) || code) + ' - the hit brings it on.';
  narrEvent(ledger, { name: label, icon: '◎', result: text, conditionApplied: code });
}

/** Everything the weapon does beyond its damage; its extra damage joins the total. */
export function applyWeaponEffects(ledger: HitLedger): void {
  const result = runProcessors(ledger);
  const landed = ledger.data.hitsAfterDefence.filter((h) => !h.ffBlocked).length;
  if (landed > 0 && !ledger.targetKilled && !ledger.alreadyDead) layExposureAfflictions(ledger);
  // AN EXPOSURE WITH USES IS SPENT BY THE HITS THAT LAND (v8.350.0): one
  // use per landed hit, gone at zero. After the effects, so this burst
  // still enjoyed it.
  if (landed > 0) replaceAll(ledger.newConds, consumeExposureUses(ledger.newConds, landed));
  ledger.totalDamage += result.extraDamage;
  result.narrativeNotes.forEach((n) => narr(ledger, n));
  ledger.containedEvents.push(...result.effectDetails);
}

/** A hit that overloaded the force field switches it off. Null when none did. */
export function forceFieldOverload(ledger: HitLedger): ForceField | null {
  if (!ledger.data.hitsAfterDefence.some((h) => h.ffOverloaded)) return null;
  const update = { ...ledger.tgt.forceField, active: false };
  narrEvent(ledger, { name: 'Force Field', icon: '⚡', result: '⚡ FORCE FIELD OVERLOADED! Field deactivated.' });
  return update;
}

/** Warded (v8.283.0) seeded the property wave: immune to power-field destruction without being natural. */
const POWER_FIELD_PROOF = ['PowerField', 'NaturalWeapon', 'WarpWeapon', 'Warded'];

/** The defender's power field on a parry of 26+: the attacker's weapon is destroyed, or immune. */
export function powerFieldParry(ledger: HitLedger): void {
  const parry = ledger.data.parryResult;
  if (!(ledger.tgt.parry?.hasPowerField && parry?.success && parry.roll >= 26)) return;
  const immune = POWER_FIELD_PROOF.some((id) => hasWS(ledger.sp, id));
  const text = immune
    ? '⚔ Power Field: Parry roll ' +
      parry.roll +
      ' (26+) - attacker weapon immune (Power Field / Natural / Warp / Warded).'
    : '⚔ Power Field: Parry roll ' + parry.roll + ' (26+) - attacker weapon DESTROYED!';
  narrEvent(ledger, { name: 'Power Field', icon: '⚔', roll: parry.roll, result: text });
}
